import { useMemo } from "react";
import { useReferralLeads } from "@/hooks/useReferralLeads";
import { useOutreachTasks } from "@/hooks/useOutreachTasks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, ResponsiveContainer, Cell } from "recharts";
import { Loader2 } from "lucide-react";
import { startOfWeek, subWeeks, format, isAfter } from "date-fns";

const STATUS_ORDER = ["New", "Contacted", "Meeting Scheduled", "Partner", "Not Interested"];
const STATUS_COLORS: Record<string, string> = {
  "New": "hsl(var(--primary))",
  "Contacted": "hsl(210 80% 55%)",
  "Meeting Scheduled": "hsl(45 90% 50%)",
  "Partner": "hsl(140 60% 45%)",
  "Not Interested": "hsl(0 0% 65%)",
};

const FUNNEL_STAGES = ["New", "Contacted", "Meeting Scheduled", "Partner"];
const FUNNEL_COLORS = ["hsl(var(--primary))", "hsl(210 80% 55%)", "hsl(45 90% 50%)", "hsl(140 60% 45%)"];

export default function Analytics() {
  const { referralLeads, loading: leadsLoading } = useReferralLeads();
  const { tasks, loading: tasksLoading } = useOutreachTasks();

  const loading = leadsLoading || tasksLoading;

  // Chart 1: Sources by Stage
  const sourcesByStage = useMemo(() => {
    const sourceTypes = [...new Set(referralLeads.map(r => r.source_type || "Unknown"))];
    return sourceTypes.map(st => {
      const row: any = { source: st.length > 20 ? st.substring(0, 18) + "…" : st };
      STATUS_ORDER.forEach(status => {
        row[status] = referralLeads.filter(r => (r.source_type || "Unknown") === st && r.status === status).length;
      });
      return row;
    }).filter(r => STATUS_ORDER.some(s => r[s] > 0));
  }, [referralLeads]);

  // Chart 2: Conversion Funnel
  const funnelData = useMemo(() => {
    const stageIndex = (s: string) => STATUS_ORDER.indexOf(s);
    return FUNNEL_STAGES.map((stage, i) => ({
      stage,
      count: referralLeads.filter(r => stageIndex(r.status) >= i).length,
    }));
  }, [referralLeads]);

  // Chart 3: Tasks completed per week (last 12 weeks)
  const tasksOverTime = useMemo(() => {
    const weeks: { label: string; start: Date; count: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const start = startOfWeek(subWeeks(new Date(), i));
      const end = startOfWeek(subWeeks(new Date(), i - 1));
      const count = tasks.filter(t => t.completed && t.completed_at && isAfter(new Date(t.completed_at), start) && !isAfter(new Date(t.completed_at), end)).length;
      weeks.push({ label: format(start, "MMM d"), start, count });
    }
    return weeks;
  }, [tasks]);

  const chartConfig = {
    ...Object.fromEntries(STATUS_ORDER.map(s => [s, { label: s, color: STATUS_COLORS[s] }])),
    count: { label: "Leads", color: "hsl(var(--primary))" },
    tasks: { label: "Tasks", color: "hsl(var(--primary))" },
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Analytics</h2>
        <p className="text-sm text-muted-foreground">Visualize your referral pipeline and outreach progress.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sources by Stage */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Referral Sources by Stage</CardTitle></CardHeader>
          <CardContent>
            {sourcesByStage.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No referral leads yet.</p>
            ) : (
              <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <BarChart data={sourcesByStage} layout="vertical" margin={{ left: 120, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="source" type="category" width={110} tick={{ fontSize: 11 }} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  {STATUS_ORDER.map(status => (
                    <Bar key={status} dataKey={status} stackId="a" fill={STATUS_COLORS[status]} />
                  ))}
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card>
          <CardHeader><CardTitle className="text-base">Conversion Funnel</CardTitle></CardHeader>
          <CardContent>
            {funnelData.every(d => d.count === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-8">No data yet.</p>
            ) : (
              <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <BarChart data={funnelData} margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {funnelData.map((_, i) => (
                      <Cell key={i} fill={FUNNEL_COLORS[i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Tasks Over Time */}
        <Card>
          <CardHeader><CardTitle className="text-base">Tasks Completed Over Time</CardTitle></CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[250px] w-full">
              <LineChart data={tasksOverTime} margin={{ left: 20, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
